// Low-poly city building generator. Each call returns a fresh randomized
// variation of the chosen kind as prefab parts — spawned as a grouped set of
// primitives (same pipeline as props), so every piece stays editable and the
// whole building moves/duplicates as one. Window rows are emissive bands
// (1 part per floor) rather than individual panes, keeping part counts ~10-20.
import { PrefabPart } from './prefabs';

export type BuildingKind = 'tower' | 'glasstower' | 'office' | 'loft' | 'apartment' | 'shop' | 'house' | 'spire';

export const BUILDING_KINDS: { kind: BuildingKind; title: string; icon: string }[] = [
  { kind: 'tower', title: 'Tower', icon: '🏙️' },
  { kind: 'glasstower', title: 'Glass Tower', icon: '🏛️' },
  { kind: 'loft', title: 'Brick Loft', icon: '🧱' },
  { kind: 'office', title: 'Office Block', icon: '🏢' },
  { kind: 'apartment', title: 'Apartment', icon: '🏬' },
  { kind: 'shop', title: 'Shop', icon: '🏪' },
  { kind: 'house', title: 'House', icon: '🏠' },
  { kind: 'spire', title: 'Spire', icon: '🗼' },
];

const BODY_COLORS = ['#16283f', '#1b3049', '#22384f', '#2a3b4d', '#101d2e', '#33475e'];
const WINDOW_COLORS = ['#3fecdb', '#ffd08a', '#9fd8ff'];
const SIGN_COLORS = ['#3fecdb', '#ff7ad9', '#ffd08a', '#9fd8ff'];
const LOFT_COLORS = ['#4e3b34', '#3d3a45', '#33475e', '#2a3b4d'];
const MULLION = '#0b1524';

const rand = (min: number, max: number) => min + Math.random() * (max - min);
const randInt = (min: number, max: number) => Math.floor(rand(min, max + 1));
const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

const box = (
  name: string,
  position: [number, number, number],
  scale: [number, number, number],
  color: string,
  extra: Partial<PrefabPart> = {},
): PrefabPart => ({ shape: 'box', name, position, scale, color, metalness: 0.5, roughness: 0.45, ...extra });

const cyl = (
  name: string,
  position: [number, number, number],
  scale: [number, number, number],
  color: string,
  extra: Partial<PrefabPart> = {},
): PrefabPart => ({ shape: 'cylinder', name, position, scale, color, metalness: 0.5, roughness: 0.45, ...extra });

const windows = (name: string, position: [number, number, number], scale: [number, number, number]): PrefabPart => ({
  shape: 'box', name, position, scale,
  color: pick(WINDOW_COLORS), emissive: rand(0.5, 1.4), metalness: 0, roughness: 0.4,
});

function tower(): PrefabPart[] {
  const W = rand(3, 5), D = rand(3, 5);
  const floors = randInt(6, 14), floorH = 0.9;
  const H = floors * floorH + 0.6;
  const body = pick(BODY_COLORS);
  const winColor = pick(WINDOW_COLORS);
  const parts: PrefabPart[] = [box('Tower Body', [0, H / 2, 0], [W, H, D], body)];
  for (let i = 0; i < floors; i++) {
    if (Math.random() < 0.12) continue; // some dark floors
    parts.push({
      shape: 'box', name: `Windows Floor ${i + 1}`,
      position: [0, i * floorH + 0.55, 0], scale: [W + 0.05, 0.35, D + 0.05],
      color: winColor, emissive: rand(0.5, 1.3), metalness: 0, roughness: 0.4,
    });
  }
  parts.push(box('Roof', [0, H + 0.15, 0], [W * 0.7, 0.3, D * 0.7], body));
  const antH = rand(1, 2.5);
  parts.push(cyl('Antenna', [W * 0.15, H + 0.3 + antH / 2, 0], [0.08, antH, 0.08], '#33475e'));
  parts.push(box('Beacon', [W * 0.15, H + 0.35 + antH, 0], [0.12, 0.12, 0.12], '#ff5a5a', { emissive: 2.5 }));
  return parts;
}

function office(): PrefabPart[] {
  const W = rand(6, 9), D = rand(4, 6);
  const floors = randInt(3, 5), floorH = 1.15;
  const H = floors * floorH + 0.4;
  const body = pick(BODY_COLORS);
  const winColor = pick(WINDOW_COLORS);
  const parts: PrefabPart[] = [box('Office Body', [0, H / 2, 0], [W, H, D], body)];
  const nStrips = Math.max(3, Math.floor(W / 1.4));
  const gap = W / nStrips;
  for (let i = 0; i < nStrips; i++) {
    const x = -W / 2 + gap * (i + 0.5);
    for (const zs of [1, -1]) {
      parts.push({
        shape: 'box', name: 'Window Strip',
        position: [x, H / 2 + 0.1, zs * (D / 2 + 0.03)], scale: [0.65, H - 1, 0.06],
        color: winColor, emissive: rand(0.4, 1.0), metalness: 0, roughness: 0.4,
      });
    }
  }
  parts.push(box('Parapet', [0, H + 0.12, 0], [W + 0.15, 0.25, D + 0.15], body));
  parts.push(box('AC Unit', [rand(-W / 4, W / 4), H + 0.45, rand(-D / 4, D / 4)], [1, 0.6, 0.8], '#2a3b4d'));
  parts.push(box('Entrance', [0, 1.1, D / 2 + 0.04], [2.2, 2.2, 0.08], '#9fd8ff', { opacity: 0.35, metalness: 0, roughness: 0.1 }));
  return parts;
}

function apartment(): PrefabPart[] {
  const W = rand(5, 7), D = rand(4, 5);
  const floors = randInt(4, 6), floorH = 1.0;
  const H = floors * floorH + 0.4;
  const body = pick(BODY_COLORS);
  const parts: PrefabPart[] = [box('Apartment Body', [0, H / 2, 0], [W, H, D], body)];
  for (let i = 0; i < floors; i++) {
    parts.push(windows(`Windows Floor ${i + 1}`, [0, i * floorH + 0.6, D / 2 + 0.03], [W * 0.85, 0.45, 0.06]));
    if (i > 0 && Math.random() < 0.7) {
      const bx = rand(-W / 3, W / 3);
      parts.push(box('Balcony', [bx, i * floorH + 0.3, D / 2 + 0.3], [1.5, 0.08, 0.6], body));
      parts.push(box('Railing', [bx, i * floorH + 0.55, D / 2 + 0.58], [1.5, 0.45, 0.04], '#3fecdb', { opacity: 0.4, metalness: 0, roughness: 0.2 }));
    }
  }
  parts.push(box('Roof', [0, H + 0.1, 0], [W + 0.2, 0.2, D + 0.2], body));
  return parts;
}

function shop(): PrefabPart[] {
  const W = rand(4, 6), D = rand(3, 4), H = rand(2.8, 3.4);
  const body = pick(BODY_COLORS);
  const sign = pick(SIGN_COLORS);
  const parts: PrefabPart[] = [
    box('Shop Body', [0, H / 2, 0], [W, H, D], body),
    box('Storefront Glass', [0, 1.0, D / 2 + 0.03], [W * 0.8, 1.9, 0.06], '#9fd8ff', { opacity: 0.3, metalness: 0, roughness: 0.05 }),
    box('Sign', [0, H - 0.35, D / 2 + 0.09], [W * 0.7, 0.55, 0.1], sign, { emissive: rand(1.5, 2.6) }),
    box('Parapet', [0, H + 0.1, 0], [W + 0.15, 0.22, D + 0.15], body),
  ];
  if (Math.random() < 0.7) {
    parts.push(box('Awning', [0, 2.1, D / 2 + 0.4], [W * 0.82, 0.06, 0.8], sign, { rotation: [0.25, 0, 0], opacity: 0.9 }));
  }
  return parts;
}

function house(): PrefabPart[] {
  const W = rand(3.6, 4.6), D = rand(3, 4), H = rand(2.6, 3.0);
  const body = pick(BODY_COLORS);
  const winColor = pick(WINDOW_COLORS);
  const wingW = W * 0.6, wingH = H * 0.8, wingD = D * 0.7;
  const wingX = W / 2 + wingW / 2 - 0.4;
  const parts: PrefabPart[] = [
    box('House Body', [0, H / 2, 0], [W, H, D], body),
    box('House Roof', [0, H + 0.08, 0], [W + 0.4, 0.16, D + 0.4], '#101d2e'),
    box('Wing', [wingX, wingH / 2, 0], [wingW, wingH, wingD], pick(BODY_COLORS)),
    box('Wing Roof', [wingX, wingH + 0.07, 0], [wingW + 0.3, 0.14, wingD + 0.3], '#101d2e'),
    box('Door', [-W / 4, 1.0, D / 2 + 0.03], [0.9, 2.0, 0.06], '#33475e'),
    box('Porch Light', [-W / 4 + 0.7, 2.1, D / 2 + 0.05], [0.1, 0.15, 0.08], '#ffd08a', { emissive: 2 }),
    windows('Front Window', [W / 5, 1.5, D / 2 + 0.03], [1.3, 0.9, 0.06]),
    box('Window Mullion V', [W / 5, 1.5, D / 2 + 0.055], [0.05, 0.9, 0.03], '#101d2e'),
    box('Window Mullion H', [W / 5, 1.5, D / 2 + 0.055], [1.3, 0.05, 0.03], '#101d2e'),
    windows('Wing Window', [wingX, 1.4, wingD / 2 + 0.03], [1.0, 0.8, 0.06]),
    box('Wing Mullion V', [wingX, 1.4, wingD / 2 + 0.055], [0.05, 0.8, 0.03], '#101d2e'),
  ];
  return parts;
}

function spire(): PrefabPart[] {
  const sections = randInt(4, 6);
  const baseR = rand(1.8, 2.4);
  const parts: PrefabPart[] = [];
  let y = 0;
  const body = pick(BODY_COLORS);
  for (let i = 0; i < sections; i++) {
    const t = i / sections;
    const r = baseR * (1 - t * 0.75);
    const h = rand(1.5, 2.4) * (1 - t * 0.3);
    parts.push(cyl(`Section ${i + 1}`, [0, y + h / 2, 0], [r * 2, h, r * 2], body));
    y += h;
    parts.push(cyl('Glow Ring', [0, y + 0.04, 0], [r * 2 * 0.92, 0.08, r * 2 * 0.92], '#3fecdb', { emissive: rand(1.5, 2.8), metalness: 0, roughness: 0.3 }));
    y += 0.08;
  }
  const tipH = rand(1.2, 2);
  parts.push(cyl('Tip', [0, y + tipH / 2, 0], [0.12, tipH, 0.12], body));
  parts.push(box('Tip Light', [0, y + tipH + 0.08, 0], [0.16, 0.16, 0.16], '#3fecdb', { emissive: 3 }));
  return parts;
}

function glassTower(): PrefabPart[] {
  // Curtain-wall skyscraper: one translucent glowing shell + crossing mullion
  // strips. N floor lines x M verticals reads as a full grid of divided panes
  // at a fraction of the part count of real panes.
  const W = rand(4, 6), D = rand(4, 6);
  const floors = randInt(8, 14), floorH = 0.95;
  const H = floors * floorH;
  const body = pick(BODY_COLORS);
  const winColor = pick(WINDOW_COLORS);
  const parts: PrefabPart[] = [
    box('Core', [0, H / 2, 0], [W - 0.3, H, D - 0.3], body),
    { shape: 'box', name: 'Glass Shell', position: [0, H / 2, 0], scale: [W, H, D],
      color: winColor, emissive: rand(0.3, 0.7), opacity: 0.45, metalness: 0, roughness: 0.1 },
  ];
  for (let i = 1; i < floors; i++) {
    parts.push(box('Floor Line', [0, i * floorH, 0], [W + 0.05, 0.09, D + 0.05], MULLION, { metalness: 0.2, roughness: 0.6 }));
  }
  const nx = Math.max(2, Math.round(W / 1.4));
  for (let k = 0; k <= nx; k++) {
    const x = -W / 2 + (k * W) / nx;
    parts.push(box('Mullion', [x, H / 2, D / 2 + 0.02], [0.08, H, 0.05], MULLION, { metalness: 0.2, roughness: 0.6 }));
    parts.push(box('Mullion', [x, H / 2, -(D / 2 + 0.02)], [0.08, H, 0.05], MULLION, { metalness: 0.2, roughness: 0.6 }));
  }
  const nz = Math.max(2, Math.round(D / 1.4));
  for (let k = 0; k <= nz; k++) {
    const z = -D / 2 + (k * D) / nz;
    parts.push(box('Mullion', [W / 2 + 0.02, H / 2, z], [0.05, H, 0.08], MULLION, { metalness: 0.2, roughness: 0.6 }));
    parts.push(box('Mullion', [-(W / 2 + 0.02), H / 2, z], [0.05, H, 0.08], MULLION, { metalness: 0.2, roughness: 0.6 }));
  }
  parts.push(box('Roof', [0, H + 0.12, 0], [W * 0.8, 0.25, D * 0.8], body));
  parts.push(box('Roof Glow', [0, H + 0.28, 0], [W * 0.5, 0.06, D * 0.5], '#3fecdb', { emissive: 1.8 }));
  return parts;
}

function loft(): PrefabPart[] {
  // Punched-window brick loft: every window is its OWN pane in a grid, some
  // lit, some dark — the classic divided-window mid-rise.
  const floors = randInt(3, 4), cols = randInt(4, 5);
  const floorH = 1.2, colW = 1.35;
  const W = cols * colW + 0.8, D = rand(4, 5);
  const H = floors * floorH + 0.5;
  const body = pick(LOFT_COLORS);
  const winColor = pick(WINDOW_COLORS);
  const parts: PrefabPart[] = [box('Loft Body', [0, H / 2, 0], [W, H, D], body, { metalness: 0.15, roughness: 0.8 })];
  for (const zs of [1, -1]) {
    for (let f = 0; f < floors; f++) {
      for (let c = 0; c < cols; c++) {
        const lit = Math.random() > 0.25;
        const x = -W / 2 + 0.4 + colW * (c + 0.5);
        parts.push({
          shape: 'box', name: `Window ${f + 1}-${c + 1}`,
          position: [x, f * floorH + 0.95, zs * (D / 2 + 0.03)], scale: [0.85, 0.75, 0.06],
          color: lit ? winColor : '#101d2e', emissive: lit ? rand(0.4, 1.1) : 0,
          metalness: 0, roughness: 0.4,
        });
        // cross mullion over the pane
        parts.push(box('Mullion', [x, f * floorH + 0.95, zs * (D / 2 + 0.055)], [0.05, 0.75, 0.03], MULLION));
      }
    }
  }
  parts.push(box('Cornice', [0, H + 0.1, 0], [W + 0.25, 0.22, D + 0.25], body, { metalness: 0.15, roughness: 0.8 }));
  parts.push(box('Entrance', [0, 1.05, D / 2 + 0.04], [1.6, 2.1, 0.08], '#33475e'));
  return parts;
}

const GENERATORS: Record<BuildingKind, () => PrefabPart[]> = {
  tower, glasstower: glassTower, office, loft, apartment, shop, house, spire,
};

export function generateBuilding(kind: BuildingKind): { title: string; parts: PrefabPart[] } {
  const meta = BUILDING_KINDS.find((b) => b.kind === kind)!;
  return { title: meta.title, parts: GENERATORS[kind]() };
}
